/**
 * AreaPage — /omraden/:slug
 *
 * Minimal calm editorial page for a public area.
 * Shows hero, name, editorial text, top intents from linked places, and linked place cards.
 *
 * Hero/gallery follows the canonical Farmly pattern:
 *   swipeable HeroCarousel ↔ PlaceCoreGalleryStrip (thumbnail sync).
 */
import { useMemo, useState } from "react";
import { usePublicAccommodationsByProducers } from "@/hooks/usePublicAccommodations";
import { AccommodationSection } from "@/components/public/AccommodationSection";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Seo } from "@/components/common";
import LandingHeader from "@/components/landing/LandingHeader";
import LandingMinimalFooter from "@/components/landing/LandingMinimalFooter";
import { Skeleton } from "@/components/ui/skeleton";
import { ContentCard } from "@/components/ui/content-card";
import { useCoverAsset } from "@/hooks/useCoverAsset";
import { useGalleryAssets } from "@/hooks/useGalleryAssets";
import { useCoverImageUrl } from "@/hooks/useMediaAssets";
import { type HeroImage } from "@/components/place/HeroCarousel";
import { PlaceCoreGalleryStrip } from "@/components/placecore/public/PlaceCoreGalleryStrip";
import { PlaceCoreHero } from "@/components/placecore/public/PlaceCoreHero";
import { FarmlyMarkdown } from "@/components/content/FarmlyMarkdown";
import { useIsMobile } from "@/hooks/use-mobile";
import { useShare } from "@/hooks/useShare";
import { formatIntent, INTENT_CONFIG } from "@/lib/placecore/public/mappings";

/* ─── Data hooks ─── */

interface AreaRow {
  id: string;
  name: string;
  slug: string;
  description_short: string | null;
  description_long: string | null;
  hero_media_asset_id: string | null;
  area_type: string;
  status: string;
}

interface LinkedPlace {
  id: string;
  name: string;
  slug: string;
  municipality: string | null;
  landskap: string | null;
  description_short: string | null;
  place_core: unknown;
}

function usePublicArea(slug: string | undefined) {
  return useQuery<AreaRow | null>({
    queryKey: ["public-area", slug],
    enabled: Boolean(slug),
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("areas")
        .select("id, name, slug, description_short, description_long, hero_media_asset_id, area_type, status")
        .eq("slug", slug!)
        .eq("status", "public")
        .maybeSingle();
      if (error) {
        console.error("[usePublicArea]", error);
        return null;
      }
      return data;
    },
  });
}

function useAreaLinkedPublicPlaces(areaId: string | undefined) {
  return useQuery<LinkedPlace[]>({
    queryKey: ["area-public-places", areaId],
    enabled: Boolean(areaId),
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("place_areas")
        .select("places!inner(id, name, slug, municipality, landskap, description_short, place_core, status)")
        .eq("area_id", areaId!)
        .order("sort_order", { ascending: true });
      if (error) {
        console.error("[useAreaLinkedPublicPlaces]", error);
        return [];
      }
      return (data ?? [])
        .map((row) => {
          const p = row.places as unknown as LinkedPlace & { status?: string };
          return p;
        })
        .filter((p) => p !== null && p.status === "public");
    },
  });
}

function useAreaAccommodations(areaId: string | undefined, placeIds: string[]) {
  return useQuery({
    queryKey: ["area-public-accommodations", areaId, placeIds],
    enabled: Boolean(areaId) && placeIds.length > 0,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      if (!areaId || placeIds.length === 0) return [];
      // Get producers linked to those places via the view
      const producerIds = new Set<string>();
      for (const pid of placeIds) {
        const { data } = await (supabase as any)
          .rpc("get_place_producers_public", { p_place_id: pid, p_limit: 50 });
        for (const row of (data ?? [])) producerIds.add(row.producer_id);
      }
      if (producerIds.size === 0) return [];

      const { data, error } = await supabase
        .from("producer_accommodations")
        .select("id, title, description_short, beds, guest_capacity, price_per_night_text, producer_id")
        .in("producer_id", [...producerIds])
        .eq("status", "public")
        .order("created_at", { ascending: false })
        .limit(3);
      if (error) throw error;
      return data ?? [];
    },
  });
}

/* ─── Intent aggregation ─── */

function aggregateTopIntents(places: LinkedPlace[], max = 6): { key: string; label: string }[] {
  const counts = new Map<string, number>();
  for (const place of places) {
    let pc: { intents?: string[] } = {};
    try {
      pc = typeof place.place_core === "string" ? JSON.parse(place.place_core) : (place.place_core as { intents?: string[] }) ?? {};
    } catch { /* ignore */ }
    const intents = pc.intents ?? [];
    for (const key of intents) {
      if (key in INTENT_CONFIG) {
        counts.set(key, (counts.get(key) ?? 0) + 1);
      }
    }
  }
  return [...counts.entries()]
    .sort((a, b) => b[1] - a[1])
    .slice(0, max)
    .map(([key]) => ({ key, label: formatIntent(key) }));
}

/* ─── Place card with cover image ─── */

function AreaPlaceCard({ place }: { place: LinkedPlace }) {
  const coverUrl = useCoverImageUrl("place", place.id, 640);
  return (
    <ContentCard
      variant="place"
      title={place.name}
      subtitle={place.municipality}
      description={place.description_short}
      imageUrl={coverUrl}
      href={`/place/${place.slug}`}
    />
  );
}

/* ─── Page ─── */

export default function AreaPage() {
  const { slug } = useParams<{ slug: string }>();
  const { data: area, isLoading: areaLoading } = usePublicArea(slug);
  const { data: linkedPlaces = [], isLoading: placesLoading } = useAreaLinkedPublicPlaces(area?.id);
  const linkedPlaceIds = useMemo(() => linkedPlaces.map((p) => p.id), [linkedPlaces]);
  const { data: areaAccommodations = [] } = useAreaAccommodations(area?.id, linkedPlaceIds);

  const isMobile = useIsMobile();
  const [heroIndex, setHeroIndex] = useState(0);
  const { share } = useShare();

  // Cover + gallery assets for hero carousel
  const { asset: coverAsset, url320: coverUrl320, url640: coverUrl640, url1280: coverUrl1280 } = useCoverAsset("area", area?.id);
  const { assets: galleryAssets, urls320: galleryUrls320, urls640: galleryUrls640 } = useGalleryAssets("area", area?.id);
  const heroUrl = useCoverImageUrl("area", area?.id, 1280);

  // Canonical shared media set (cover first, then gallery) used by both hero and thumbnail strip.
  const sharedMedia = useMemo(() => {
    const media: { id: string; full: string; thumb: string; alt: string }[] = [];
    const seen = new Set<string>();

    const push = (
      asset: { id: string; alt_text?: string | null } | null,
      fullUrl: string | null,
      thumbUrl: string | null,
    ) => {
      if (!asset || !fullUrl || !thumbUrl || seen.has(asset.id)) return;
      seen.add(asset.id);
      media.push({
        id: asset.id,
        full: fullUrl,
        thumb: thumbUrl,
        alt: asset.alt_text ?? area?.name ?? "",
      });
    };

    push(
      coverAsset,
      coverUrl1280 ?? coverUrl640 ?? coverUrl320,
      coverUrl320 ?? coverUrl640 ?? coverUrl1280,
    );

    for (let i = 0; i < galleryAssets.length; i++) {
      push(
        galleryAssets[i],
        galleryUrls640[i] ?? null,
        galleryUrls320[i] ?? galleryUrls640[i] ?? null,
      );
    }

    return media;
  }, [
    coverAsset,
    coverUrl320,
    coverUrl640,
    coverUrl1280,
    galleryAssets,
    galleryUrls320,
    galleryUrls640,
    area?.name,
  ]);

  const heroImages: HeroImage[] = useMemo(
    () => sharedMedia.map((m) => ({ id: m.id, src: m.full, alt: m.alt })),
    [sharedMedia],
  );

  const stripThumbUrls = useMemo(() => sharedMedia.map((m) => m.thumb), [sharedMedia]);
  const stripFullUrls = useMemo(() => sharedMedia.map((m) => m.full), [sharedMedia]);
  const topIntents = useMemo(() => aggregateTopIntents(linkedPlaces), [linkedPlaces]);
  const regionLabel = useMemo(() => {
    const counts = new Map<string, number>();
    for (const place of linkedPlaces) {
      const region = place.landskap?.trim();
      if (!region) continue;
      counts.set(region, (counts.get(region) ?? 0) + 1);
    }
    let selected: string | null = null;
    let maxCount = 0;
    for (const [region, count] of counts.entries()) {
      if (count > maxCount) {
        selected = region;
        maxCount = count;
      }
    }
    return selected;
  }, [linkedPlaces]);

  const areaSubtitle = regionLabel ? `Område i ${regionLabel}` : "Område";

  const handleShare = () => {
    if (!area) return;
    share({
      kind: "area",
      name: area.name,
      slug: area.slug,
      landskap: regionLabel,
    }, "area_page");
  };

  const isLoading = areaLoading;

  // Not found
  if (!isLoading && !area) {
    return (
      <div className="flex min-h-screen flex-col">
        <LandingHeader variant="lens" />
        <main className="flex-1 container max-w-2xl px-5 pt-24 pb-24">
          <h1 className="font-display text-xl font-semibold text-foreground mb-2">Området finns inte</h1>
          <p className="text-muted-foreground">Vi kunde inte hitta det här området.</p>
        </main>
        <LandingMinimalFooter />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col">
      {area && (
        <Seo
          title={`${area.name} – Farmly`}
          description={area.description_short || `Utforska ${area.name} på Farmly`}
          ogType="website"
          image={heroUrl}
          imageAlt={`${area.name} – omslagsbild`}
        />
      )}
      <LandingHeader variant="lens" />

      <main className="flex-1">
        {/* Hero — canonical shared carousel with place-like rhythm */}
        {isLoading ? (
          <div className="w-full aspect-[21/9] bg-muted animate-pulse" />
        ) : heroImages.length > 0 ? (
          <PlaceCoreHero
            title={area?.name ?? ""}
            subtitle={areaSubtitle}
            descriptionShort={area?.description_short}
            heroImageUrl={heroUrl}
            heroImages={heroImages}
            onShare={handleShare}
            mobileShareInHero
            activeIndex={isMobile ? heroIndex : undefined}
            onHeroIndexChange={setHeroIndex}
          />
        ) : (
          <div className="container max-w-3xl px-5 pt-16 pb-4 space-y-3">
            <h1 className="font-display text-3xl md:text-4xl font-semibold text-foreground">{area?.name}</h1>
            <p className="text-sm text-muted-foreground">{areaSubtitle}</p>
            {area?.description_short && (
              <p className="text-base text-muted-foreground leading-relaxed">{area.description_short}</p>
            )}
            <div>
              <button
                type="button"
                onClick={handleShare}
                className="inline-flex items-center rounded-full border border-border px-3 py-1.5 text-sm text-foreground hover:bg-muted transition-colors"
              >
                Dela
              </button>
            </div>
          </div>
        )}

        {/* Breadcrumb context */}
        {!isLoading && area && regionLabel && (
          <section className="container max-w-3xl px-5 pt-3">
            <p className="text-sm text-muted-foreground">
              <span>{regionLabel}</span>
              <span className="mx-2">→</span>
              <span className="text-foreground/80">{area.name}</span>
            </p>
          </section>
        )}

        {/* Gallery strip — synced with hero carousel */}
        {stripThumbUrls.length > 1 && (
          <section className="container max-w-3xl px-5 pt-6">
            <PlaceCoreGalleryStrip
              thumbUrls={stripThumbUrls}
              fullUrls={stripFullUrls}
              activeHeroIndex={isMobile ? heroIndex : undefined}
              onSelectIndex={isMobile ? setHeroIndex : undefined}
            />
          </section>
        )}

        {/* Editorial text */}
        <div className="container max-w-3xl px-5 pt-8 space-y-4">
          {isLoading ? (
            <>
              <Skeleton className="h-5 w-full" />
              <Skeleton className="h-5 w-3/4" />
            </>
          ) : (
            <>
              {area?.description_short && (
                <p className="text-lg text-muted-foreground leading-relaxed">
                  {area.description_short}
                </p>
              )}
              {area?.description_long && (
                <FarmlyMarkdown content={area.description_long} className="text-base" />
              )}
            </>
          )}
        </div>

        {/* Top intents — "Passar bra för" */}
        {!placesLoading && topIntents.length > 0 && (
          <section className="container max-w-3xl px-5 pt-10">
            <h2 className="font-display text-lg font-medium text-foreground mb-3">Passar bra för</h2>
            <div className="flex flex-wrap gap-2">
              {topIntents.map((intent) => (
                <span
                  key={intent.key}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-secondary text-secondary-foreground text-sm"
                >
                  {intent.label}
                </span>
              ))}
            </div>
          </section>
        )}

        {/* Linked places */}
        {(placesLoading || linkedPlaces.length > 0) && (
          <section className="container max-w-3xl px-5 pt-10 pb-4">
            <h2 className="font-display text-lg font-medium text-foreground mb-4">
              Platser i {area?.name ?? "området"}
            </h2>
            {placesLoading ? (
              <div className="space-y-4">
                <Skeleton className="h-48 w-full rounded-xl" />
                <Skeleton className="h-48 w-full rounded-xl" />
              </div>
            ) : (
              <div className="flex flex-col gap-6">
                {linkedPlaces.map((place) => (
                  <AreaPlaceCard key={place.id} place={place} />
                ))}
              </div>
            )}
          </section>
        )}

        {/* Accommodation — "Stanna här" */}
        {areaAccommodations.length > 0 && (
          <section className="container max-w-3xl px-5 pt-10 pb-4">
            <AccommodationSection
              accommodations={areaAccommodations}
              title="Stanna här"
            />
          </section>
        )}

        {/* Empty — no places linked */}
        {!placesLoading && linkedPlaces.length === 0 && area && (
          <section className="container max-w-3xl px-5 pt-10 pb-4">
            <p className="text-sm text-muted-foreground">
              Det finns inga platser kopplade till {area.name} just nu.
            </p>
          </section>
        )}

        {/* Breathing room */}
        <div className="h-12 sm:h-20" />
      </main>

      <LandingMinimalFooter />
    </div>
  );
}
